import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import * as E from '@react-email/components'
import { eq } from 'drizzle-orm'
import { data, redirect, Link, useFetcher, href } from 'react-router'
import { z } from 'zod'
import {
	AuthField,
	AuthShell,
	AuthSubmit,
} from '#app/components/auth/auth-shell.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Honeypot } from '#app/components/forms.tsx'
import { db } from '#app/db/index.ts'
import { users } from '#app/db/schema/index.ts'
import { enforceAbuseLimit } from '#app/utils/abuse-limit.server.ts'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { enqueueEmail } from '#app/utils/email-dispatch.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getIpAddress } from '#app/utils/request-info.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { noIndexMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/forgot-password.ts'
import { getRedirectToUrl, prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const ForgotPasswordSchema = z.object({
	email: EmailSchema,
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	await enforceAbuseLimit('forgot-password', getIpAddress(request))

	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema,
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { email } = submission.value

	const user = await db.query.users.findFirst({
		where: eq(users.email, email),
		columns: { email: true },
	})

	if (user) {
		const { verifyUrl, redirectTo, otp } = await prepareVerification({
			period: 10 * 60,
			request,
			type: 'reset-password',
			target: email,
		})

		await enqueueEmail({
			type: 'password-reset',
			to: user.email,
			subject: `Syrcha Password Reset`,
			react: <ForgotPasswordEmail resetUrl={verifyUrl.toString()} otp={otp} />,
		})

		return redirect(redirectTo.toString())
	}

	const redirectTo = getRedirectToUrl({
		request,
		type: 'reset-password',
		target: email,
	})
	return redirect(redirectTo.toString())
}

function ForgotPasswordEmail({
	resetUrl,
	otp,
}: {
	resetUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Syrcha Password Reset</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link:</E.Text>
				</p>
				<E.Link href={resetUrl}>{resetUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: Route.MetaFunction = () => {
	return noIndexMeta('Forgot password · Syrcha')
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()
	const isPending = forgotPassword.state === 'submitting'

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<AuthShell
			eyebrow="/ password · reset"
			title={
				<>
					Forgot your <span className="accent-underline">password</span>?
				</>
			}
			intro="Tell us the email on the account and we'll send a one-time code plus a magic link to reset it."
			footer={
				<Link
					to={href('/login')}
					className="inline-flex items-center gap-1.5 text-[var(--marketing-gray)] transition-colors hover:text-[var(--marketing-black)]"
				>
					<span aria-hidden>←</span>
					Back to sign in
				</Link>
			}
		>
			<forgotPassword.Form method="POST" {...getFormProps(form)}>
				<Honeypot />

				<AuthField
					id={fields.email.id}
					label="Account email"
					error={fields.email.errors?.[0]}
				>
					<input
						{...getInputProps(fields.email, { type: 'email' })}
						autoFocus
						autoComplete="email"
						placeholder="you@example.com"
						className="auth-input lowercase"
					/>
				</AuthField>

				<ErrorList errors={form.errors} id={form.errorId} announce />

				<AuthSubmit isPending={isPending} pendingLabel="Sending…">
					Send reset link
				</AuthSubmit>
			</forgotPassword.Form>
		</AuthShell>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
